import { isApiError } from "../api/types";

const REPORTED_TO_SENTRY_SYMBOL = Symbol.for("nommia.reportedToSentry");

export type ErrorReporter = {
  captureException: (
    error: unknown,
    context?: {
      tags?: Record<string, string>;
      extra?: Record<string, unknown>;
      fingerprint?: string[];
    }
  ) => unknown;
};

export type ErrorReportContext = {
  mechanism?:
    | "api"
    | "react-query"
    | "app"
    | "global-error"
    | "unhandledrejection"
    | "console-error";
  handled?: boolean;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
};

type GlobalErrorTarget = {
  addEventListener: (type: string, listener: (event: Event) => void) => void;
  removeEventListener: (type: string, listener: (event: Event) => void) => void;
};

type ConsoleErrorReporter = {
  error: (...args: unknown[]) => void;
};

type GlobalErrorReportingOptions = {
  target?: GlobalErrorTarget;
  console?: ConsoleErrorReporter;
};

const UNKNOWN_EXCEPTION_MESSAGE = "Unexpected non-error exception captured.";

const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return new Error(error);
  }

  return new Error(UNKNOWN_EXCEPTION_MESSAGE);
};

const markAsReported = (error: Error) => {
  try {
    Object.defineProperty(error, REPORTED_TO_SENTRY_SYMBOL, {
      value: true,
      configurable: true,
    });
  } catch {
    Object.assign(error, {
      [REPORTED_TO_SENTRY_SYMBOL]: true,
    });
  }
};

export const hasBeenReportedToSentry = (error: unknown) =>
  error instanceof Error &&
  (error as Error & { [REPORTED_TO_SENTRY_SYMBOL]?: boolean })[
    REPORTED_TO_SENTRY_SYMBOL
  ] === true;

const buildTags = (error: Error, context: ErrorReportContext) => {
  const tags: Record<string, string> = {
    error_name: error.name,
    ...context.tags,
  };

  if (context.mechanism) {
    tags.mechanism = context.mechanism;
  }

  if (context.handled !== undefined) {
    tags.handled = String(context.handled);
  }

  if (isApiError(error)) {
    if (error.status !== undefined) {
      tags.api_status = String(error.status);
    }

    if (error.errorCode) {
      tags.api_error_code = error.errorCode;
    }

    if (error.errorName) {
      tags.api_error_name = error.errorName;
    }

    if (error.requestId) {
      tags.api_request_id = error.requestId;
    }
  }

  return tags;
};

const buildExtra = (error: Error, context: ErrorReportContext) => {
  const extra: Record<string, unknown> = {
    ...context.extra,
  };

  if (isApiError(error)) {
    if (error.status !== undefined) {
      extra.status = error.status;
    }

    if (error.requestId) {
      extra.requestId = error.requestId;
    }

    if (error.errorCode) {
      extra.errorCode = error.errorCode;
    }

    if (error.errorName) {
      extra.errorName = error.errorName;
    }

    if (error.errors) {
      extra.apiErrors = error.errors;
    }

    if (error.data !== undefined) {
      extra.apiData = error.data;
    }
  }

  return extra;
};

export const reportError = (
  reporter: ErrorReporter,
  error: unknown,
  context: ErrorReportContext = {}
) => {
  const exception = toError(error);

  if (hasBeenReportedToSentry(exception)) {
    return exception;
  }

  reporter.captureException(exception, {
    tags: buildTags(exception, context),
    extra: buildExtra(exception, context),
    fingerprint: context.fingerprint,
  });

  markAsReported(exception);
  return exception;
};

const getErrorEventException = (event: Event) => {
  const errorEvent = event as ErrorEvent;

  return (
    errorEvent.error ??
    (errorEvent.message ? new Error(errorEvent.message) : event)
  );
};

const getUnhandledRejectionException = (event: Event) => {
  const rejectionEvent = event as PromiseRejectionEvent;

  return rejectionEvent.reason ?? new Error("Unhandled promise rejection");
};

const buildConsoleErrorException = (args: unknown[]) => {
  const [firstArg] = args;

  if (firstArg instanceof Error) {
    return firstArg;
  }

  if (typeof firstArg === "string" && firstArg.trim().length > 0) {
    return new Error(firstArg);
  }

  return new Error("console.error was called");
};

export const installGlobalErrorReporting = (
  reporter: ErrorReporter,
  options: GlobalErrorReportingOptions = {}
) => {
  const target =
    options.target ?? (typeof window !== "undefined" ? window : undefined);
  const consoleReporter =
    options.console ?? (typeof console !== "undefined" ? console : undefined);

  const onError = (event: Event) => {
    const errorEvent = event as ErrorEvent;

    reportError(reporter, getErrorEventException(event), {
      mechanism: "global-error",
      handled: false,
      extra: {
        message: errorEvent.message,
        filename: errorEvent.filename,
        lineno: errorEvent.lineno,
        colno: errorEvent.colno,
      },
    });
  };

  const onUnhandledRejection = (event: Event) => {
    reportError(reporter, getUnhandledRejectionException(event), {
      mechanism: "unhandledrejection",
      handled: false,
    });
  };

  if (target) {
    target.addEventListener("error", onError);
    target.addEventListener("unhandledrejection", onUnhandledRejection);
  }

  const originalConsoleError = consoleReporter?.error;

  if (consoleReporter && originalConsoleError) {
    consoleReporter.error = (...args: unknown[]) => {
      reportError(reporter, buildConsoleErrorException(args), {
        mechanism: "console-error",
        handled: true,
        extra: {
          arguments: args,
        },
      });

      originalConsoleError.apply(consoleReporter, args);
    };
  }

  return () => {
    if (target) {
      target.removeEventListener("error", onError);
      target.removeEventListener("unhandledrejection", onUnhandledRejection);
    }

    if (consoleReporter && originalConsoleError) {
      consoleReporter.error = originalConsoleError;
    }
  };
};
